import { useState } from 'react';
import { useReveal } from '@/hooks/useReveal';

const industries = [
  {
    name: 'Retail Shops',
    icon: 'ri-store-2-line',
    pain: 'Customers walk out because staff are too busy to attend. No follow-up system means lost repeat business.',
    challenge: 'Managing hundreds of product queries daily while handling walk-in customers.',
    solution: 'AI chatbot handles product queries, sends catalogs, and collects orders 24/7. Auto follow-ups recover abandoned inquiries.',
    results: '3x lead conversion | 60% fewer missed inquiries',
  },
  {
    name: 'Garment Stores',
    icon: 'ri-t-shirt-line',
    pain: 'New arrivals go unnoticed. Customers don\'t know about sales and offers until it\'s too late.',
    challenge: 'Showcasing new collections to the right audience at the right time.',
    solution: 'Broadcast new arrivals with images and catalogs. Segment customers by preference and purchase history.',
    results: '45% higher campaign engagement | 2x repeat purchase rate',
  },
  {
    name: 'Supermarkets',
    icon: 'ri-shopping-cart-2-line',
    pain: 'Daily offers and deals get lost in crowded social media feeds. Customer loyalty is hard to maintain.',
    challenge: 'Communicating time-sensitive offers and building a loyal customer base.',
    solution: 'Send personalized daily deals via WhatsApp. Digital loyalty cards and automated replenishment reminders.',
    results: '4x offer redemption rate | 35% increase in repeat visits',
  },
  {
    name: 'Restaurants',
    icon: 'ri-restaurant-line',
    pain: 'Peak hour chaos means missed calls and lost table bookings. No-shows hurt revenue daily.',
    challenge: 'Managing reservations, takeout orders, and customer inquiries simultaneously.',
    solution: 'AI handles table bookings, order confirmations, and automated no-show reminders. Digital menus shared instantly.',
    results: '70% fewer no-shows | 50% faster order processing',
  },
  {
    name: 'Cafes',
    icon: 'ri-cup-line',
    pain: 'Regular customers aren\'t properly recognized or rewarded. New menu items go unnoticed.',
    challenge: 'Building a community feel while scaling customer communication.',
    solution: 'Loyalty program integration, new menu announcements, and personalized offers based on order history.',
    results: '50% higher loyalty program enrollment | 25% increase in average order value',
  },
  {
    name: 'Dental Clinics',
    icon: 'ri-tooth-line',
    pain: 'Patients forget appointments. Manual reminder calls waste staff time. Follow-up care instructions get lost.',
    challenge: 'Managing appointment schedules, patient follow-ups, and treatment reminders.',
    solution: 'Automated appointment booking, 3-stage reminders, post-treatment care instructions, and review requests.',
    results: '80% fewer no-shows | 5+ hours saved per week on calls',
  },
  {
    name: 'Clinics',
    icon: 'ri-heart-pulse-line',
    pain: 'Patients struggle to book appointments. Test results and prescriptions require multiple follow-ups.',
    challenge: 'Streamlining patient communication while maintaining a personal, caring touch.',
    solution: 'WhatsApp appointment booking, automated test result notifications, prescription reminders, and health tips.',
    results: '60% faster appointment booking | 90% patient satisfaction rate',
  },
  {
    name: 'Hospitals',
    icon: 'ri-hospital-line',
    pain: 'Patient communication is fragmented across departments. Discharge instructions get missed.',
    challenge: 'Coordinating multi-department communication at scale.',
    solution: 'Centralized patient communication hub. Automated discharge instructions, follow-up scheduling, and feedback collection.',
    results: '40% reduction in readmissions | 55% improvement in patient compliance',
  },
  {
    name: 'Schools',
    icon: 'ri-graduation-cap-line',
    pain: 'Parents miss important announcements. Fee reminders and event updates require constant manual effort.',
    challenge: 'Keeping hundreds of parents informed without overwhelming admin staff.',
    solution: 'Bulk parent announcements, fee payment reminders with payment links, event RSVPs, and report card notifications.',
    results: '95% parent engagement | 80% reduction in admin calls',
  },
  {
    name: 'Colleges',
    icon: 'ri-building-4-line',
    pain: 'Admission inquiries flood in during peak season. Student communication is scattered across channels.',
    challenge: 'Managing thousands of admission queries and ongoing student communication.',
    solution: 'AI admission assistant handles FAQs, application status updates, fee reminders, and exam notifications.',
    results: '70% faster query resolution | 3x admission inquiry conversion',
  },
  {
    name: 'Tuition Centres',
    icon: 'ri-book-open-line',
    pain: 'Class schedules, homework updates, and progress reports require constant parent communication.',
    challenge: 'Keeping parents updated on student progress without spending hours on messaging.',
    solution: 'Automated class reminders, homework notifications, progress reports, and fee collection via WhatsApp.',
    results: '90% parent satisfaction | 5+ hours saved weekly on communication',
  },
  {
    name: 'Travel Agencies',
    icon: 'ri-flight-takeoff-line',
    pain: 'Travel packages get lost in email spam. Last-minute changes cause panic calls.',
    challenge: 'Delivering time-sensitive travel information instantly.',
    solution: 'Send itinerary updates, booking confirmations, visa reminders, and travel tips via WhatsApp with rich media.',
    results: '98% message open rate | 45% increase in package bookings',
  },
  {
    name: 'Real Estate',
    icon: 'ri-home-4-line',
    pain: 'Property inquiries sit unanswered. Site visit follow-ups are forgotten. Leads go cold.',
    challenge: 'Nurturing high-value property leads through long decision cycles.',
    solution: 'Auto-respond to property inquiries with brochures, virtual tours, and site visit scheduling. Drip nurture sequences.',
    results: '3x lead-to-visit conversion | 60% shorter sales cycle',
  },
  {
    name: 'Builders & Construction',
    icon: 'ri-tools-line',
    pain: 'Project updates, material orders, and client approvals require endless back-and-forth.',
    challenge: 'Managing communication across clients, contractors, and suppliers.',
    solution: 'Automated project status updates, material order confirmations, payment requests, and milestone notifications.',
    results: '50% faster approvals | 35% reduction in communication delays',
  },
  {
    name: 'Automobile Dealers',
    icon: 'ri-car-line',
    pain: 'Test drive follow-ups are inconsistent. Service reminders get forgotten. Trade-in inquiries are lost.',
    challenge: 'Managing the entire vehicle lifecycle communication — from inquiry to service.',
    solution: 'Automated test drive booking, service reminders, insurance renewal alerts, and trade-in valuation offers.',
    results: '2x test drive bookings | 40% increase in service visits',
  },
  {
    name: 'Car Service Centres',
    icon: 'ri-tools-fill',
    pain: 'Customers forget service schedules. Pickup and drop coordination is chaotic.',
    challenge: 'Keeping customers on schedule and managing logistics efficiently.',
    solution: 'Automated service reminders, pickup/drop scheduling, real-time service status updates, and digital invoices.',
    results: '55% increase in on-time services | 45% reduction in no-shows',
  },
  {
    name: 'Jewellery Stores',
    icon: 'ri-vip-diamond-line',
    pain: 'High-value customers expect VIP treatment. New collections need exclusive previews.',
    challenge: 'Maintaining premium, personalized communication for discerning clients.',
    solution: 'Exclusive collection previews via WhatsApp, personalized design consultations, and automated occasion-based reminders.',
    results: '4x VIP engagement | 60% increase in referral sales',
  },
  {
    name: 'Insurance Agents',
    icon: 'ri-shield-check-line',
    pain: 'Policy renewals get missed. Claim processes confuse customers. Leads go cold after initial contact.',
    challenge: 'Nurturing long-term client relationships and managing policy lifecycles.',
    solution: 'Automated renewal reminders, claim assistance chatbot, policy document sharing, and cross-sell recommendations.',
    results: '85% renewal rate | 2x policy cross-sell conversion',
  },
  {
    name: 'Financial Consultants',
    icon: 'ri-funds-line',
    pain: 'Market updates need instant delivery. Investment opportunities have narrow windows.',
    challenge: 'Delivering timely financial advice and maintaining client trust.',
    solution: 'Real-time market alerts, portfolio updates, appointment scheduling, and secure document sharing.',
    results: '3x client engagement | 70% faster consultation booking',
  },
  {
    name: 'Education Consultants',
    icon: 'ri-global-line',
    pain: 'Thousands of study-abroad inquiries come during peak season. Most go unanswered.',
    challenge: 'Handling massive inquiry volumes with a small team during admission seasons.',
    solution: 'AI answers course, visa, and university queries 24/7. Auto-assign hot leads to the right counselor.',
    results: '5x inquiry handling capacity | 2x enrollment conversion',
  },
  {
    name: 'HR Agencies',
    icon: 'ri-group-line',
    pain: 'Candidate communication is slow. Interview scheduling is a nightmare. Offer letters get delayed.',
    challenge: 'Managing high-volume candidate pipelines efficiently.',
    solution: 'Automated interview scheduling, status updates, document collection, and offer letter distribution via WhatsApp.',
    results: '60% faster hiring cycle | 80% candidate satisfaction',
  },
  {
    name: 'Manufacturing',
    icon: 'ri-building-2-line',
    pain: 'Order status updates require manual calls. Supplier coordination is fragmented.',
    challenge: 'Keeping distributors and clients updated on production and delivery timelines.',
    solution: 'Automated order confirmations, production status updates, dispatch notifications, and invoice sharing.',
    results: '45% fewer status inquiry calls | 30% faster order processing',
  },
  {
    name: 'Wholesale',
    icon: 'ri-stack-line',
    pain: 'Bulk order communication is slow. Price list updates don\'t reach retailers in time.',
    challenge: 'Managing B2B order communication across hundreds of retailers.',
    solution: 'Bulk price list sharing, order confirmation automation, stock availability alerts, and payment reminders.',
    results: '3x faster order processing | 50% reduction in communication overhead',
  },
  {
    name: 'Distribution',
    icon: 'ri-truck-line',
    pain: 'Delivery updates, stock availability, and retailer coordination require constant manual effort.',
    challenge: 'Keeping the entire supply chain informed in real-time.',
    solution: 'Automated dispatch notifications, delivery tracking, stock alerts, and retailer order confirmation.',
    results: '65% fewer delivery inquiries | 40% improvement in retailer satisfaction',
  },
];

export default function IndustrySolutions() {
  const { ref, visible } = useReveal();
  const [expandedIndustry, setExpandedIndustry] = useState<number | null>(null);

  return (
    <section id="industries" className="relative py-20 md:py-28 bg-background-50 overflow-hidden">
      <div ref={ref} className={`max-w-7xl mx-auto px-4 md:px-6 reveal ${visible ? 'visible' : ''}`}>
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <span className="inline-block px-4 py-1.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold uppercase tracking-wider mb-5">
            Industry Solutions
          </span>
          <h2 className="text-3xl md:text-5xl font-bold font-heading text-foreground-950 mb-5 leading-tight">
            Built for{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-accent-500">
              Your Industry
            </span>
          </h2>
          <p className="text-lg text-foreground-600 leading-relaxed max-w-2xl mx-auto">
            Every industry has unique communication challenges. DGFlow adapts to yours — with pre-built
            workflows, templates, and automation designed specifically for your business type.
          </p>
        </div>

        {/* Industry Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
          {industries.map((industry, i) => (
            <div
              key={industry.name}
              onClick={() => setExpandedIndustry(expandedIndustry === i ? null : i)}
              className={`group cursor-pointer bg-background-50 border rounded-2xl p-5 transition-all duration-400 hover:-translate-y-1 ${
                expandedIndustry === i
                  ? 'border-primary-300/60 shadow-lg shadow-primary-500/5 ring-1 ring-primary-200/40'
                  : 'border-background-200/70 hover:border-primary-200/40'
              }`}
            >
              {/* Header */}
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors duration-400 ${
                  expandedIndustry === i ? 'bg-primary-100' : 'bg-background-100 group-hover:bg-primary-50'
                }`}>
                  <i className={`${industry.icon} text-lg ${
                    expandedIndustry === i ? 'text-primary-600' : 'text-foreground-500'
                  }`}></i>
                </div>
                <h3 className="text-sm font-bold font-heading text-foreground-950">{industry.name}</h3>
                <i className={`ri-arrow-down-s-line text-foreground-400 ml-auto transition-transform duration-300 ${
                  expandedIndustry === i ? 'rotate-180' : ''
                }`}></i>
              </div>

              {/* Pain preview */}
              <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                {industry.pain}
              </p>

              {/* Expanded content */}
              <div className={`overflow-hidden transition-all duration-500 ${
                expandedIndustry === i ? 'max-h-[400px] opacity-100 mt-3' : 'max-h-0 opacity-0'
              }`}>
                <div className="pt-3 border-t border-background-200/70 space-y-3">
                  <div>
                    <p className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wider mb-1">Challenge</p>
                    <p className="text-xs text-foreground-700 leading-relaxed">{industry.challenge}</p>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-primary-500 uppercase tracking-wider mb-1">How DGFlow Solves It</p>
                    <p className="text-xs text-foreground-700 leading-relaxed">{industry.solution}</p>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-accent-600 uppercase tracking-wider mb-1">Expected Results</p>
                    <p className="text-xs font-semibold text-accent-700">{industry.results}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}